import { PerformanceReports } from '@cobliteam/api-lib';
import IncidentsRequester from '@cobliteam/api-lib/src/dashboard/incidents/IncidentsRequester';
import type { DateRange, Incident, IncidentsPayload, TimestampInMillis } from '@cobliteam/common-types';
import Environment from '@cobliteam/react-environments';
import { type UseQueryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getIncidentsRequester = () => new IncidentsRequester({ host: env.apiHost });
const getReportsRequester = () => new PerformanceReports({ host: env.herbieApiHost });

const QUERY_KEY = 'INCIDENTS';

const useGetIncidents = (payload: IncidentsPayload) => {
  return useInfiniteQuery<Incident[]>({
    queryKey: [QUERY_KEY, payload],
    initialPageParam: Date.now(),
    queryFn: async ({ pageParam = Date.now() }) =>
      await getIncidentsRequester().getIncidents({
        ...payload,
        endTime: pageParam as TimestampInMillis,
      }),
    getNextPageParam: (lastPage, pages) => {
      const prevPage = pages[pages.length - 1];
      const lastPageDate = prevPage[prevPage.length - 1].date;

      if (lastPage.length === payload.amount) {
        return lastPageDate;
      }

      return undefined;
    },
  });
};

const useDownloadIncidentsReport = ({ start, end }: DateRange, options?: Partial<UseQueryOptions<Blob>>) => {
  return useQuery<Blob>({
    queryKey: [QUERY_KEY, 'REPORT'],
    queryFn: async () => await getReportsRequester().getIncidentsReport(start, end),
    refetchOnMount: false,
    ...options,
  });
};

export { useGetIncidents, useDownloadIncidentsReport };
